import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "./AuthRouter/AuthContext";
import {
  CallGETAPI,
  CallPOSTAPI,
  storeAuthSession,
} from "../helper/Constants";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const logoUrl = `${process.env.PUBLIC_URL || ""}/callmafia-logo.png`;
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await CallPOSTAPI("api/login-v1", {
        ...formData,
        url: window.location.origin,
      });

      if (response?.data?.status) {
        let token = response?.data?.token;
        storeAuthSession(token);
        toast.success("Login Successful");
        await CallGETAPI("api/connect");
        login();
        navigate("/dashboard");
      } else {
        toast.error(
          response?.data?.message ||
            response?.msg ||
            "Login failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("An unexpected error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-red-50 via-stone-50 to-rose-100 dark:from-black dark:via-[#120607] dark:to-[#22090d] ${isLoading ? "opacity-90" : ""}`}>
      <ToastContainer />
      <main className="flex items-center justify-center min-h-screen py-8 px-4">
        <div className="w-full max-w-5xl">
          <div className="grid overflow-hidden rounded-3xl border border-red-100 bg-white shadow-[0_24px_60px_rgba(127,29,29,0.16)] dark:border-red-950/40 dark:bg-[#100607] dark:shadow-[0_28px_70px_rgba(127,29,29,0.28)] lg:grid-cols-[0.95fr_1.05fr]">
            <div className="hidden bg-[radial-gradient(circle_at_top,_rgba(220,38,38,0.16),_transparent_28%),linear-gradient(180deg,_#120405_0%,_#090304_100%)] p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-12">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-200/70">
                  CallMafia Admin
                </p>
                <div className="mt-8 flex items-center justify-center">
                  <div className="overflow-hidden rounded-full border border-red-700/60 bg-black shadow-[0_24px_60px_rgba(127,29,29,0.38)]">
                    <img
                      src={logoUrl}
                      alt="CallMafia logo"
                      className="h-64 w-64 object-cover"
                    />
                  </div>
                </div>
                <h2 className="mt-10 text-4xl font-bold tracking-tight text-white">
                  Admin access with clear branding on the left.
                </h2>
                <p className="mt-5 text-base leading-8 text-zinc-300">
                  This keeps the form area clean while making the logo stand out properly on desktop.
                </p>
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 text-sm text-white/72">
                  Large CallMafia logo preview
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 text-sm text-white/72">
                  Same color family as the rest of the panel
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 text-sm text-white/72">
                  No login functionality changed
                </div>
              </div>
            </div>

            <div className="px-8 py-6">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="mx-auto mb-5 overflow-hidden rounded-[22px] border border-red-200 bg-white shadow-[0_12px_30px_rgba(127,29,29,0.14)] dark:border-red-900/40 dark:bg-black lg:hidden">
                  <img
                    src={logoUrl}
                    alt="CallMafia logo"
                    className="h-20 w-20 object-cover"
                  />
                </div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-red-600 dark:text-red-300">
                  Administration Portal
                </p>
                <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">
                  Login to Your Account
                </h1>
                <p className="text-sm text-gray-600 dark:text-zinc-300">
                  Enter your username & password to login
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Username Field */}
                <div>
                  <label 
                    htmlFor="email" 
                    className="mb-2 block text-sm font-medium text-gray-700 dark:text-zinc-200"
                  >
                    Username
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-zinc-500">
                      @
                    </span>
                    <input
                      type="text"
                      name="email"
                      id="email"
                      onChange={handleChange}
                      value={formData.email}
                      required
                      className="w-full rounded-xl border border-red-100 bg-red-50/40 py-2.5 pl-10 pr-4 text-gray-900 transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-950/40 dark:bg-black/40 dark:text-white"
                      placeholder="Enter your username"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label 
                    htmlFor="yourPassword" 
                    className="mb-2 block text-sm font-medium text-gray-700 dark:text-zinc-200"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      id="yourPassword"
                      onChange={handleChange}
                      value={formData.password}
                      required
                      className="w-full rounded-xl border border-red-100 bg-red-50/40 py-2.5 pl-4 pr-12 text-gray-900 transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-950/40 dark:bg-black/40 dark:text-white"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 transition-colors hover:text-red-700 focus:outline-none dark:text-zinc-500 dark:hover:text-red-300"
                    >
                      {showPassword ? (
                        <FaEye className="w-5 h-5" />
                      ) : (
                        <FaEyeSlash className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-red-700 via-red-600 to-rose-500 px-4 py-2.5 font-medium text-white shadow-[0_18px_30px_rgba(127,29,29,0.22)] transition-colors duration-200 hover:from-red-600 hover:via-red-500 hover:to-rose-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:from-red-300 disabled:via-red-300 disabled:to-rose-300"
                  >
                    {isLoading ? (
                      <>
                        <svg 
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24"
                        >
                          <circle 
                            className="opacity-25" 
                            cx="12" 
                            cy="12" 
                            r="10" 
                            stroke="currentColor" 
                            strokeWidth="4"
                          ></circle>
                          <path 
                            className="opacity-75" 
                            fill="currentColor" 
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </button>
                </div>

                {/* Register Link */}
                <div className="text-center pt-2">
                  <p className="text-sm text-gray-600 dark:text-zinc-300">
                    Don't have an account?{" "}
                    <Link 
                      to="/register" 
                      className="font-medium text-red-700 transition-colors hover:text-red-600 hover:underline dark:text-red-300 dark:hover:text-red-200"
                    >
                      Register
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* Optional: Add a footer or additional info */}
          <div className="text-center mt-6">
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              © 2026 CallMafia. All rights reserved.
            </p>
          </div>
        </div>
      </main>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-2xl border border-red-100 bg-white p-6 shadow-xl dark:border-red-950/40 dark:bg-[#100607]">
            <div className="flex items-center space-x-3">
              <svg 
                className="h-8 w-8 animate-spin text-red-600" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                ></circle>
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="font-medium text-gray-700 dark:text-zinc-200">Processing...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
